<script lang="ts">
  import { page } from '$app/stores';
  import { STATIC_URL } from '$lib/constants';
  import { gameSearchTracking } from '$lib/tracking';
  import { analytics } from '$lib/utils';
  import { afterUpdate, createEventDispatcher } from 'svelte';

  const dispatch = createEventDispatcher();

  import EmptyState from '$components/EmptyState.svelte';
  import FadeScroll from '$components/FadeScroll/FadeScroll.svelte';
  import TagChip from '$components/mds/TagChip/TagChip.svelte';
  import Button from '$mds/Buttons/Button';
  import LoadingSpinner from '$mds/LoadingSpinner';
  import { fade } from 'svelte/transition';
  import CoachItem from './CoachItem.svelte';
  import GameItem from './GameItem.svelte';
  import GroupItem from './GroupItem.svelte';
  import SessionItem from './SessionItem.svelte';
  import GuideItem from './GuideItem.svelte';
  import EventItem from './EventItem.svelte';

  export let query = '';
  export let isFetching = false;
  export let results = {};
  export let elements = [];
  export let currentGame;
  export let lightGameHover = false;
  export let hasSeeAll = true;
  export let selectedFilter = 'all';

  let parentEl = null;
  let selectedElementIdx = null;
  let resultsValues = [];

  $: totalCount = Object.values(results).reduce((total, { indexUid, totalHits }) => {
    if (currentGame && indexUid.includes('games')) {
      return total;
    }
    return total + totalHits;
  }, 0);
  $: noResults = !isFetching && !totalCount;

  $: {
    resultsValues = currentGame ? Object.values(results).slice().reverse() : Object.values(results);
  }

  // In case of a different label than the index name
  const searchItemsLabel = {
    games: 'games',
    accounts: 'partners',
    groups: 'Communities',
    product_coaching: 'sessions',
    product_guides: 'guides',
    product_events: 'events'
  };

  export function focus() {
    if (elements.length > 0) {
      selectedElementIdx = 0;
      elements[selectedElementIdx].focus();
    }
  }

  function handleGamePress(game) {
    // This will be the case when a user is on a games page, then removes the games chip from the search query,
    // and then clicks on the same game in the search results
    analytics.track({
      name: gameSearchTracking.TRACK_GAME_SEARCH_RESULT_CLICKED,
      props: { gameTitle: game?.title?.en }
    });
    if (game?.slug !== $page.params?.game) return;
    // currentGame.set(game);
  }

  function handleKeydown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedElementIdx = selectedElementIdx >= elements.length - 1 ? 0 : selectedElementIdx + 1;
      elements[selectedElementIdx].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedElementIdx = selectedElementIdx > 0 ? selectedElementIdx - 1 : elements.length - 1;
      elements[selectedElementIdx].focus();
    } else if (e.key === 'Enter') {
      e.stopPropagation();
    } else {
      dispatch('searchInputFocus');
    }
  }

  afterUpdate(() => {
    elements = [...(parentEl?.querySelectorAll('a:not([href^="/search"])') || [])];
  });
</script>

<div
  bind:this={parentEl}
  class="space-y-4 py-6 {isFetching || noResults ? 'min-h-[calc(100svh-200px)] md:min-h-[400px]' : ''} relative"
>
  <FadeScroll padding="" gradientColor="from-ondark-low" orientation="horizontal" mouseDrag class="">
    <div class="mb-4 mt-1 flex w-full justify-center gap-2">
      <TagChip
        variant={selectedFilter === 'all' ? 'selected' : 'default'}
        on:click={() => {
          dispatch('filterByIndex', { indexName: 'all' });
          selectedFilter = 'all';
        }}
      >
        <span class="{selectedFilter === 'all' ? 'text-ondark-primary' : 'text-ondark-subtle'} text-body-xs capitalize"
          >All</span
        >
      </TagChip>
      {#each resultsValues as result (result.indexName)}
        {#if result.totalHits > 0 && (!currentGame || result.indexName !== 'games') && result.indexName !== 'accounts'}
          <TagChip
            variant={selectedFilter === result.indexName ? 'selected' : 'default'}
            on:click={() => {
              dispatch('filterByIndex', { indexName: result.indexName });
              selectedFilter = result.indexName;
            }}
          >
            <span
              class="{selectedFilter === result.indexName
                ? 'text-ondark-primary'
                : 'text-ondark-subtle'} text-body-xs capitalize"
              >{searchItemsLabel[result.indexName] || result.indexName}</span
            >
          </TagChip>
        {/if}
      {/each}
    </div>
  </FadeScroll>

  <div class="overflow-y-scroll md:max-h-[calc(90svh-220px)]">
    {#if noResults}
      <EmptyState
        kind="large"
        illustration="{STATIC_URL}/illustrations/thisisfine.svg"
        showBg={false}
        padding="p-2"
        imageSize="max-h-40"
      >
        <svelte:fragment slot="content">
          <p class="text-body-2xs text-balance">
            <span>Oops! We didn't find anything with</span>
            <span class="text-ondark-primary">"{query}"</span>
          </p>
        </svelte:fragment>
      </EmptyState>
    {:else}
      {#each resultsValues as result, index (result.indexName)}
        {#if result.hitsPerPage > 0 && result.totalHits > 0 && (!currentGame || result.indexName !== 'games')}
          <section>
            <div class="text-ondark-secondary mt-6 flex items-center justify-between space-x-2">
              <span class="text-body-sm">
                {result.totalHits}{result.totalHits >= 1000 ? '+' : ''}
                {searchItemsLabel[result.indexName] || result.indexName}
              </span>
              {#if hasSeeAll}
                <Button size="sm" variant="ghost" width="hug" on:click={() => dispatch('clickAll', result.indexName)}>
                  See all
                </Button>
              {/if}
            </div>

            <ul class="animate-drop-in-slow" data-sveltekit-preload-data="false">
              {#each result.hits as hit (hit.id || hit.objectID)}
                <li class="result-item hover:bg-ondark-medium rounded-lg">
                  {#if result.indexName === 'games'}
                    <GameItem
                      on:click={() => {
                        handleGamePress(hit);
                      }}
                      game={hit}
                      class="hover:!bg-ondark-medium"
                      on:keydown={handleKeydown}
                      lighterHover={lightGameHover}
                      on:click={() => dispatch('item:click')}
                    />
                  {:else if result.indexName === 'accounts'}
                    <CoachItem
                      class="hover:bg-ondark-medium"
                      coach={hit}
                      on:keydown={handleKeydown}
                      on:click={() => dispatch('item:click')}
                    />
                  {:else if result.indexName === 'groups'}
                    <GroupItem
                      class="hover:bg-ondark-medium"
                      group={hit}
                      on:keydown={handleKeydown}
                      on:click={() => dispatch('item:click')}
                    />
                  {:else if result.indexName === 'product_guides'}
                    <GuideItem
                      class="hover:bg-ondark-medium"
                      guide={hit}
                      on:keydown={handleKeydown}
                      on:click={() => dispatch('item:click')}
                    />
                  {:else if result.indexName === 'product_coaching'}
                    <SessionItem
                      class="hover:bg-ondark-medium"
                      session={hit}
                      on:keydown={handleKeydown}
                      on:click={() => dispatch('item:click')}
                    />
                  {:else if result.indexName === 'product_events'}
                    <EventItem
                      class="hover:bg-ondark-medium"
                      event={hit}
                      on:keydown={handleKeydown}
                      on:click={() => dispatch('item:click')}
                    />
                  {/if}
                </li>
              {/each}
            </ul>
          </section>
        {/if}
      {/each}
    {/if}
  </div>
</div>
{#if isFetching}
  <div
    in:fade={{ delay: 400, duration: 1000 }}
    out:fade={{ delay: 0, duration: 250 }}
    class="bg-ondark-overlay absolute inset-0 flex items-center justify-center"
  >
    <LoadingSpinner theme="brand" size="lg" />
  </div>
{/if}

<style lang="postcss">
  .result-item :global(.highlight em) {
    @apply text-action-ondark-base not-italic;
  }
  .result-item :global(.highlight.text-ondark-subtle em) {
    @apply text-action-ondark-active font-semibold not-italic;
  }
</style>
